import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import { Dropdown } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  DownOutlined,
  FileAddOutlined,
  NotificationOutlined,
  PlusOutlined,
  ReadOutlined,
} from '@ant-design/icons';

import { IconBookOpen, IconBug, IconCircleHelp, IconClipboardList, IconLightbulb } from './feedback/feedback-icons';
import { openHubFeedback, type FeedbackView } from './feedback/feedback-open';
import { useFeedbackGate } from './feedback/useFeedbackGate';
import { NewArticleModal } from './NewArticleModal';

/**
 * Оранжевая «Создать ▾» в шапке HUB 2.0 (план wk6 §1.1).
 *   «Написать в УК»: те же пункты и подписи, что в плавающем «+» (HelpFab):
 *   вопрос в УК / ошибка / идея / мои обращения — открывают его форму или список
 *   (CRM «Обращения»); «Задать вопрос по базе знаний» → /knowledge/ask.
 *   «Создать» (редакторам / УК): новая статья (NewArticleModal), новый курс
 *   (мастер /admin/courses/new), новая публикация — в CRM «Новости» (новости
 *   пишутся только через CRM с правами news:*).
 */
const CRM_NEWS_URL = 'https://crm.brasol.ru/news';

interface Row {
  key: string;
  label: string;
  hint?: string;
  icon: ReactNode;
  color: string;
  soft: string;
  onClick: () => void;
}

export function CreateMenu({ compact }: { compact?: boolean }): JSX.Element {
  const navigate = useNavigate();
  const { me, feedbackOn, questionOn } = useFeedbackGate();
  const [open, setOpen] = useState(false);
  const [articleOpen, setArticleOpen] = useState(false);

  const pick = (fn: () => void) => (): void => {
    setOpen(false);
    fn();
  };

  const req = (view: FeedbackView, label: string, hint: string, icon: ReactNode, color: string, soft: string): Row => ({
    key: view,
    label,
    hint,
    icon,
    color,
    soft,
    onClick: pick(() => openHubFeedback(view)),
  });

  // Пункты и порядок — как в плавающем «+»; CRM может выключить виджет роли или только «Вопрос в УК».
  const write: Row[] = [
    ...(questionOn ? [req('question', 'Вопрос в УК', 'доступы, документы, любая просьба', <IconCircleHelp size={16} />, 'var(--fk-info)', 'var(--fk-info-soft)')] : []),
    ...(feedbackOn
      ? [
          req('bug', 'Сообщить об ошибке', 'что-то не работает или показывает неверно', <IconBug size={16} />, 'var(--fk-error)', 'var(--fk-error-soft)'),
          req('idea', 'Предложить идею', 'идея для базы, курсов или HUB', <IconLightbulb size={16} />, 'var(--fk-success-text)', 'var(--fk-success-soft)'),
          req('mine', 'Мои обращения', 'статусы и ответы', <IconClipboardList size={16} />, 'var(--fk-indigo)', 'color-mix(in srgb, var(--fk-indigo) 10%, transparent)'),
        ]
      : []),
    {
      key: 'question-kb',
      label: 'Задать вопрос по базе знаний',
      hint: 'ответ может стать статьёй FAQ',
      icon: <IconBookOpen size={16} />,
      color: 'var(--fk-chart-5)',
      soft: 'color-mix(in srgb, var(--fk-chart-5) 14%, transparent)',
      onClick: pick(() => navigate('/knowledge/ask')),
    },
  ];

  const create: Array<{ key: string; label: string; icon: ReactNode; onClick: () => void }> = [
    ...(me?.canCreate || me?.canAdmin ? [{ key: 'article', label: 'Новая статья', icon: <FileAddOutlined />, onClick: pick(() => setArticleOpen(true)) }] : []),
    ...(me?.canAdmin
      ? [
          { key: 'course', label: 'Новый курс', icon: <ReadOutlined />, onClick: pick(() => navigate('/admin/courses/new')) },
          { key: 'news', label: 'Новая публикация', icon: <NotificationOutlined />, onClick: pick(() => window.open(CRM_NEWS_URL, '_blank', 'noopener')) },
        ]
      : []),
  ];

  const panel = (
    <div className="fk-hub-pop" style={{ minWidth: 300 }} role="menu">
      <div className="fk-hub-pop-title">Написать в УК</div>
      {write.map((r) => (
        <button key={r.key} type="button" role="menuitem" className="fk-hub-pop-row" onClick={r.onClick}>
          <span className="fk-hub-pop-ic" style={{ background: r.soft, color: r.color }}>{r.icon}</span>
          <span style={{ lineHeight: 1.25, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{r.label}</span>
            {r.hint && <span style={{ display: 'block', fontSize: 11, color: 'var(--fk-text-muted)' }}>{r.hint}</span>}
          </span>
        </button>
      ))}
      {create.length > 0 && (
        <>
          <div className="fk-hub-pop-sep" />
          <div className="fk-hub-pop-title">Создать</div>
          {create.map((c) => (
            <button key={c.key} type="button" role="menuitem" className="fk-hub-pop-row" onClick={c.onClick} style={{ fontSize: 13, fontWeight: 600 }}>
              <span style={{ color: 'var(--fk-text-2)', fontSize: 16, width: 30, display: 'inline-flex', justifyContent: 'center' }}>{c.icon}</span>
              {c.label}
            </button>
          ))}
        </>
      )}
    </div>
  );

  return (
    <>
      <Dropdown open={open} onOpenChange={setOpen} trigger={['click']} placement="bottomRight" popupRender={() => panel}>
        <button type="button" className="fk-hub-create" title="Создать: обращение, ошибка, идея" data-tour="create" aria-haspopup="menu" aria-expanded={open}>
          <PlusOutlined />
          {!compact && <span className="fk-hub-create-label">Создать</span>}
          <DownOutlined style={{ fontSize: 11, opacity: 0.8 }} />
        </button>
      </Dropdown>
      {articleOpen && <NewArticleModal open={articleOpen} onClose={() => setArticleOpen(false)} />}
    </>
  );
}
